import { service } from '@ember/service'
import Component from '@glimmer/component'
import { colors } from '@colors'
import { formatNumber } from 'teacher-ui-client/utils/giraffe/formatter'
import { t, type IntlService } from 'ember-intl'
import type { TitleOptions } from 'highcharts'
import {
  SeriesColumn,
  type ChartSpacing,
  type SeriesColumnOptions,
} from 'teacher-ui-client/components/giraffe/primitives/series-column'
import { LeftSplitChart } from 'teacher-ui-client/components/giraffe/left-split-chart'
import { SummaryBox } from 'teacher-ui-client/components/giraffe/summary-box'
import { SummaryBoxColorScheme } from 'teacher-ui-client/components/giraffe/summary-box/themes'
import type { StoriesWrittenStudentData } from 'teacher-ui-client/utils/student-events/fetchers/stories-written-student'

interface Args {
  summaryTitle: string
  data: StoriesWrittenStudentData
  link: {
    route: string
    text: string
  }
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class StoriesWrittenStudent extends Component<Signature> {
  @service
  intl!: IntlService

  title: TitleOptions = {
    text: this.intl.t('reporting.chartTitles.storiesWrittenStudent'),
  }

  get summaryStats(): { label: string; value: string }[] {
    const totalCount = this.args.data.summary.total_count
    return [
      {
        label: this.intl.t('reporting.charts.storiesWritten.TotalStoriesWrittenSummaryStudent'),
        value: formatNumber(totalCount, 1),
      },
    ]
  }

  colorScheme: SummaryBoxColorScheme = SummaryBoxColorScheme.LightBlue

  chartSpacing: ChartSpacing = [10, 10, 10, 10]

  get columns(): SeriesColumnOptions[] {
    const { events } = this.args.data
    const data = events.map((monthData, index) => {
      return { x: index, y: monthData.count, monthData }
    })
    return [{ name: 'count', data, color: colors.oceanyBlue300, type: 'column' }]
  }

  get categories(): string[] {
    const { events } = this.args.data
    return events.map((monthData) => monthData.month)
  }

  <template>
    <div data-test-giraffe-chart-stories-written-student class="relative h-full" ...attributes>
      <LeftSplitChart>
        <:left>
          <SummaryBox
            class="h-full"
            @title={{@summaryTitle}}
            @stats={{this.summaryStats}}
            @link={{@link}}
            @colorScheme={{this.colorScheme}}
          />
        </:left>
        <:right>
          <SeriesColumn
            @title={{this.title}}
            @data={{this.columns}}
            @categories={{this.categories}}
            @stackingStyle="normal"
            @yAxisLabel={{t "reporting.charts.storiesWritten.numberOfStoriesWritten"}}
            @chartSpacing={{this.chartSpacing}}
          />
        </:right>
      </LeftSplitChart>
    </div>
  </template>
}

export default StoriesWrittenStudent
